import React, { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Loader2, Save, FolderKanban, User, CheckCircle2, MessagesSquare } from "lucide-react";
import { api } from "../api";
import { StatusPill, PriorityPill, Avatar, formatDate } from "./common";
import { toast } from "../hooks/use-toast";
import ChatPanel from "./ChatPanel";

const pct = (v) => (v == null ? 0 : v <= 1 ? Math.round(v * 100) : Math.round(v));

export default function TaskDetailSheet({ taskId, open, onOpenChange, onSaved }) {
  const [task, setTask] = useState(null);
  const [form, setForm] = useState({});
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open || !taskId) return;
    setLoading(true);
    api.get(`/tasks/${taskId}`)
      .then((r) => {
        setTask(r.data);
        setForm({
          title: r.data.title || "",
          status: r.data.status || "",
          priority: r.data.priority || "",
          assigneeId: r.data.assigneeId || "unassigned",
          startDate: r.data.startDate || "",
          dueDate: r.data.dueDate || "",
          percentComplete: pct(r.data.percentComplete),
          description: r.data.description || "",
          notes: r.data.notes || "",
        });
      })
      .catch((e) => toast({ title: "Failed to load task", description: e?.response?.data?.detail || e.message }))
      .finally(() => setLoading(false));
  }, [open, taskId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, percentComplete: Number(form.percentComplete) };
      const { data } = await api.put(`/tasks/${taskId}`, payload);
      setTask(data);
      toast({ title: "Task updated", description: "Your changes have been saved." });
      onSaved && onSaved(data);
      onOpenChange(false);
    } catch (e) {
      toast({ title: "Update failed", description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        {loading || !task ? (
          <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-red-500" /></div>
        ) : (
          <>
            <SheetHeader className="text-left">
              <div className="mb-1 flex items-center gap-2">
                <StatusPill value={task.status} />
                <PriorityPill value={task.priority} />
              </div>
              <SheetTitle className="pr-6 text-lg leading-snug">{task.title}</SheetTitle>
            </SheetHeader>

            <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-slate-100 pb-4 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><FolderKanban className="h-4 w-4" /> {task.project || "\u2014"}</span>
              <span className="flex items-center gap-1.5"><User className="h-4 w-4" /> {task.assignee || "Unassigned"}</span>
            </div>

            <div className="mt-5 space-y-5">
              <div>
                <Label className="text-xs font-semibold text-slate-500">Task name</Label>
                <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-slate-500">Status</Label>
                  <Select value={form.status} onValueChange={(v) => set("status", v)}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {task.statusChoices.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-500">Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {task.priorityChoices.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-xs font-semibold text-slate-500">Assigned to</Label>
                <Select value={form.assigneeId} onValueChange={(v) => set("assigneeId", v)}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {team.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        <span className="flex items-center gap-2">
                          <span className="flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold text-white" style={{ backgroundColor: m.color }}>{m.initials}</span>
                          {m.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-slate-500">Start date</Label>
                  <Input type="date" value={form.startDate ? String(form.startDate).slice(0, 10) : ""} onChange={(e) => set("startDate", e.target.value)} className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-500">Due date</Label>
                  <Input type="date" value={form.dueDate ? String(form.dueDate).slice(0, 10) : ""} onChange={(e) => set("dueDate", e.target.value)} className="mt-1.5" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold text-slate-500">Progress</Label>
                  <span className="text-sm font-semibold text-slate-700">{form.percentComplete}%</span>
                </div>
                <input type="range" min="0" max="100" step="5" value={form.percentComplete}
                  onChange={(e) => set("percentComplete", e.target.value)}
                  className="mt-2 w-full accent-red-600" />
              </div>

              <div>
                <Label className="text-xs font-semibold text-slate-500">Description</Label>
                <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className="mt-1.5" placeholder="Add a description..." />
              </div>

              <div>
                <Label className="text-xs font-semibold text-slate-500">History / Notes / Issues</Label>
                <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className="mt-1.5" placeholder="Add notes..." />
              </div>

              {task.dateComplete && (
                <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700">
                  <CheckCircle2 className="h-4 w-4" /> Completed on {formatDate(task.dateComplete)}
                </div>
              )}

              <div className="border-t border-slate-100 pt-4">
                <h4 className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-900"><MessagesSquare className="h-4 w-4 text-[#d0333a]" /> Comments</h4>
                <ChatPanel scope="task" refId={task.id} refName={task.title} ownerId={task.assigneeId} compact />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3 border-t border-slate-100 pt-4">
              <button onClick={() => onOpenChange(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={save} disabled={saving} className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-60">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
